// Stream health and latency counters.
//
// Exposed as window.__webremoteStats() so the e2e tests can assert on real
// pipeline behaviour (frames actually decoded, gaps, keyframe requests, latency)
// instead of guessing from pixels, and so a debug overlay can show the same
// numbers the native client's lag graph does.

const MAX_SAMPLES = 300;

const counters = {
	state: "idle",
	framesReceived: 0,
	bytesReceived: 0,
	framesDecoded: 0,
	framesDropped: 0,
	packetGaps: 0,
	idrRequests: 0,
	reconnects: 0,
	decodeQueueSize: 0,
	framesFed: 0,
	framesSkippedNoKey: 0,
	lastFrameAt: 0,
	codecUnsupported: null,
};

// Ring of glass-to-glass samples in ms.
const latency = [];

function record(sample) {
	latency.push(sample);
	if (latency.length > MAX_SAMPLES) {
		latency.shift();
	}
}

function percentile(sorted, p) {
	if (sorted.length === 0) return null;
	const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
	return Math.round(sorted[idx]);
}

export function setState(state) {
	counters.state = state;
}

// glassToGlassMs is derived from the sender's frameTime clock, so it is only
// meaningful when the clocks are synced (true on the local harness, assumed by
// the native client too). Pass null when frameTime is absent.
export function countFrame(bytes, glassToGlassMs) {
	counters.framesReceived++;
	counters.bytesReceived += bytes;
	counters.lastFrameAt = Date.now();
	if (glassToGlassMs !== null && isFinite(glassToGlassMs)) {
		record(glassToGlassMs);
	}
}

export function countGap() {
	counters.packetGaps++;
}

export function countIDRRequest() {
	counters.idrRequests++;
}

export function countReconnect() {
	counters.reconnects++;
}

export function setCodecUnsupported(codec) {
	counters.codecUnsupported = codec;
}

export function setDecoderStats({decoded, dropped, queue, fed, skippedNoKey}) {
	counters.framesDecoded = decoded;
	counters.framesDropped = dropped;
	counters.decodeQueueSize = queue;
	counters.framesFed = fed;
	counters.framesSkippedNoKey = skippedNoKey;
}

export function reset() {
	Object.assign(counters, {
		framesReceived: 0,
		bytesReceived: 0,
		framesDecoded: 0,
		framesDropped: 0,
		packetGaps: 0,
		idrRequests: 0,
		reconnects: 0,
		decodeQueueSize: 0,
		framesFed: 0,
		framesSkippedNoKey: 0,
		lastFrameAt: 0,
		codecUnsupported: null,
	});
	latency.length = 0;
}

export function state() {
	return counters.state;
}

export function lastFrameAt() {
	return counters.lastFrameAt;
}

export function framesDecoded() {
	return counters.framesDecoded;
}

export function msSinceLastFrame() {
	return counters.lastFrameAt ? Date.now() - counters.lastFrameAt : null;
}

// Cheap enough to call on every input tick, unlike snapshot().
export function hasDecodedFrame() {
	return counters.framesDecoded > 0;
}

export function snapshot() {
	const sorted = [...latency].sort((a, b) => a - b);
	return {
		...counters,
		msSinceLastFrame: counters.lastFrameAt ? Date.now() - counters.lastFrameAt : null,
		glassToGlassMs: {
			p50: percentile(sorted, 50),
			p99: percentile(sorted, 99),
			samples: sorted.length,
		},
	};
}

if (typeof window !== "undefined") {
	window.__webremoteStats = snapshot;
}
